import { useState, useEffect, useCallback } from "react";
import {
  AlertTypes,
  setAlert,
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  MuiIcons,
  FormChipsControlled,
} from "@leap4/frontend";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../interfaces/root-state.interface";
import { urlService } from "../../routing/urlsService";
import ReportHeader from "../common/ReportHeader/ReportHeader";
import { AXIOS_INSTANCE } from "../../../api/axiosInstance";

const { Security: SecurityIcon, Save: SaveIcon } = MuiIcons;

interface Campaign {
  id_campaign: string;
  name: string | null;
  description: string | null;
}

interface Organization {
  id: string;
  name: string;
}

const mapStateToProps = (state: RootState) => ({
  selectedOrganization: state.auth.selectedOrganizationId,
});

const mapActions = {
  setAlert,
};

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

const AccessManagerModule = (props: PropsFromRedux) => {
  const { setAlert: dispatchAlert } = props;
  const [tabIndex, setTabIndex] = useState(0);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>("");
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [savedOrgIds, setSavedOrgIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [orgCampaignIds, setOrgCampaignIds] = useState<string[]>([]);
  const [savedOrgCampaignIds, setSavedOrgCampaignIds] = useState<string[]>([]);
  const [loadingOrgCampaigns, setLoadingOrgCampaigns] = useState(false);
  const [savingOrgCampaigns, setSavingOrgCampaigns] = useState(false);

  const hasChanges =
    selectedOrgIds.length !== savedOrgIds.length ||
    selectedOrgIds.some((id) => !savedOrgIds.includes(id));

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await AXIOS_INSTANCE.get("/campaigns");
      setCampaigns(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
      dispatchAlert({ msg: "Could not fetch campaigns", type: AlertTypes.FAILURE });
    }
  }, [dispatchAlert]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await AXIOS_INSTANCE.get("/campaign-access/organizations");
      setOrganizations(response.data || []);
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
      dispatchAlert({ msg: "Could not fetch organizations", type: AlertTypes.FAILURE });
    }
  }, [dispatchAlert]);

  const fetchCampaignAccess = useCallback(async (campaignId: string) => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const response = await AXIOS_INSTANCE.get(`/campaign-access/by-campaign/${campaignId}`);
      const accessData = response.data.organizations || [];
      const ids = accessData.map((a: { organizationId: string }) => a.organizationId);
      setSelectedOrgIds(ids);
      setSavedOrgIds(ids);
    } catch (err) {
      console.error("Failed to fetch campaign access:", err);
      dispatchAlert({ msg: "Could not fetch campaign access", type: AlertTypes.FAILURE });
    } finally {
      setLoading(false);
    }
  }, [dispatchAlert]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCampaigns(), fetchOrganizations()]);
      setLoading(false);
    };
    init();
  }, [fetchCampaigns, fetchOrganizations]);

  useEffect(() => {
    if (selectedCampaign && organizations.length > 0) {
      fetchCampaignAccess(selectedCampaign);
    }
  }, [selectedCampaign, organizations, fetchCampaignAccess]);

  const fetchOrgCampaigns = useCallback(async (orgId: string) => {
    if (!orgId) return;
    setLoadingOrgCampaigns(true);
    try {
      const response = await AXIOS_INSTANCE.get(`/campaign-access/by-organization/${orgId}`);
      const ids = response.data.campaignIds || [];
      setOrgCampaignIds(ids);
      setSavedOrgCampaignIds(ids);
    } catch (err) {
      console.error("Failed to fetch org campaigns:", err);
      dispatchAlert({ msg: "Could not fetch campaigns for organization", type: AlertTypes.FAILURE });
    } finally {
      setLoadingOrgCampaigns(false);
    }
  }, [dispatchAlert]);

  useEffect(() => {
    if (selectedOrg) fetchOrgCampaigns(selectedOrg);
  }, [selectedOrg, fetchOrgCampaigns]);

  const handleCampaignChange = (campaignId: string) => {
    if (hasChanges) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to switch campaigns?")) {
        return;
      }
    }
    setSelectedCampaign(campaignId);
  };

  const handleChipsUpdate = (items: string[]) => {
    setSelectedOrgIds(items);
  };

  const handleSave = async () => {
    if (!selectedCampaign) return;
    setSaving(true);
    try {
      await AXIOS_INSTANCE.put(`/campaign-access/by-campaign/${selectedCampaign}`, {
        organizationIds: selectedOrgIds,
      });

      setSavedOrgIds([...selectedOrgIds]);
      dispatchAlert({ msg: "Campaign access saved successfully", type: AlertTypes.SUCCESS });
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to save campaign access";
      dispatchAlert({ msg: message, type: AlertTypes.FAILURE });
    } finally {
      setSaving(false);
    }
  };

  const controlledValue = selectedOrgIds
    .map((id) => organizations.find((org) => org.id === id))
    .filter(Boolean) as Organization[];

  const orgCampaignsHasChanges =
    orgCampaignIds.length !== savedOrgCampaignIds.length ||
    orgCampaignIds.some((id) => !savedOrgCampaignIds.includes(id));

  const campaignChipOptions = campaigns.map((c) => ({
    id_campaign: c.id_campaign,
    label: c.name || c.id_campaign,
  }));

  const controlledOrgCampaignValue = orgCampaignIds
    .map((id) => campaignChipOptions.find((c) => c.id_campaign === id))
    .filter(Boolean) as { id_campaign: string; label: string }[];

  const handleOrgCampaignChipsUpdate = (items: string[]) => {
    setOrgCampaignIds(items);
  };

  const handleSaveOrgCampaigns = async () => {
    if (!selectedOrg) return;
    setSavingOrgCampaigns(true);
    try {
      const addedCampaigns = orgCampaignIds.filter((id) => !savedOrgCampaignIds.includes(id));
      const removedCampaigns = savedOrgCampaignIds.filter((id) => !orgCampaignIds.includes(id));

      for (const campaignId of addedCampaigns) {
        const existing = await AXIOS_INSTANCE.get(`/campaign-access/by-campaign/${campaignId}`);
        const currentOrgs = (existing.data.organizations || []).map(
          (o: { organizationId: string }) => o.organizationId
        );
        if (!currentOrgs.includes(selectedOrg)) {
          await AXIOS_INSTANCE.put(`/campaign-access/by-campaign/${campaignId}`, {
            organizationIds: [...currentOrgs, selectedOrg],
          });
        }
      }

      for (const campaignId of removedCampaigns) {
        const existing = await AXIOS_INSTANCE.get(`/campaign-access/by-campaign/${campaignId}`);
        const currentOrgs = (existing.data.organizations || []).map(
          (o: { organizationId: string }) => o.organizationId
        );
        await AXIOS_INSTANCE.put(`/campaign-access/by-campaign/${campaignId}`, {
          organizationIds: currentOrgs.filter((id: string) => id !== selectedOrg),
        });
      }

      setSavedOrgCampaignIds([...orgCampaignIds]);
      dispatchAlert({ msg: "Organization campaign access saved", type: AlertTypes.SUCCESS });
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to save organization campaign access";
      dispatchAlert({ msg: message, type: AlertTypes.FAILURE });
    } finally {
      setSavingOrgCampaigns(false);
    }
  };

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          {
            link: urlService.accessManager(),
            text: "Access Manager",
            icon: <SecurityIcon />,
          },
        ]}
      />
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ mx: 2, mb: 1, textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Access Manager
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage which organizations have access to each campaign.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ margin: "10px", padding: "20px", borderRadius: "12px" }} elevation={1}>
            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 3 }}>
              <Tab label="By Campaign" />
              <Tab label="By Organization" />
            </Tabs>

            {tabIndex === 0 && (
              <>
                <Autocomplete
                  options={campaigns}
                  getOptionLabel={(option) =>
                    option.name
                      ? `${option.id_campaign} - ${option.name}`
                      : option.id_campaign
                  }
                  value={campaigns.find((c) => c.id_campaign === selectedCampaign) || null}
                  onChange={(_, newValue) => handleCampaignChange(newValue?.id_campaign || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Campaign" variant="outlined" />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id_campaign === val.id_campaign}
                  fullWidth
                  sx={{ mb: 3 }}
                  ListboxProps={{ style: { overflowY: "scroll" } }}
                />

                {selectedCampaign && (
                  <>
                    <Divider sx={{ mb: 3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle1">
                        Organizations with Access
                        {hasChanges && (
                          <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                            (unsaved changes)
                          </Typography>
                        )}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                      >
                        Save Changes
                      </Button>
                    </Box>

                    {loading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <FormChipsControlled
                        valueKey="id"
                        labelKey="name"
                        inputName="Add an organization"
                        optionsList={organizations}
                        enableCreate={false}
                        pureCreate={false}
                        onUpdateItems={handleChipsUpdate}
                        disableCloseOnSelect={true}
                        controlledValue={controlledValue}
                        stackProps={{ width: "100%", style: { margin: "10px 0" } }}
                      />
                    )}
                  </>
                )}

                {!selectedCampaign && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">
                      Select a campaign to manage organization access.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {tabIndex === 1 && (
              <>
                <Autocomplete
                  options={organizations}
                  getOptionLabel={(option) => option.name}
                  value={organizations.find((o) => o.id === selectedOrg) || null}
                  onChange={(_, newValue) => setSelectedOrg(newValue?.id || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Organization" variant="outlined" />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  fullWidth
                  sx={{ mb: 3 }}
                  ListboxProps={{ style: { overflowY: "scroll" } }}
                />

                {selectedOrg && (
                  <>
                    <Divider sx={{ mb: 3 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle1">
                        Campaigns accessible by{" "}
                        <strong>{organizations.find((o) => o.id === selectedOrg)?.name}</strong>
                        {orgCampaignsHasChanges && (
                          <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                            (unsaved changes)
                          </Typography>
                        )}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={savingOrgCampaigns ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSaveOrgCampaigns}
                        disabled={savingOrgCampaigns || !orgCampaignsHasChanges}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                      >
                        Save Changes
                      </Button>
                    </Box>

                    {loadingOrgCampaigns ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <FormChipsControlled
                        valueKey="id_campaign"
                        labelKey="label"
                        inputName="Add a campaign"
                        optionsList={campaignChipOptions}
                        enableCreate={false}
                        pureCreate={false}
                        onUpdateItems={handleOrgCampaignChipsUpdate}
                        disableCloseOnSelect={true}
                        controlledValue={controlledOrgCampaignValue}
                        stackProps={{ width: "100%", style: { margin: "10px 0" } }}
                      />
                    )}
                  </>
                )}

                {!selectedOrg && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">
                      Select an organization to view its campaign access.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default connector(AccessManagerModule);
